/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IInternalTelemetry, InternalTelemetryLogOptions, ITelemetry, LogLevel } from '@msdyn365-commerce/telemetry-internal';
import { getCurrency } from 'locale-currency';
import { IRequestContext } from '../interfaces';
import msdyn365Commerce from '../msdyn365-commerce';

export interface IContentEditableInitEvent {
    type: string;
}

// Helper function for parsing/normalizing json, exported
export const parsePayload = (content: string) => {
    try {
        return content.constructor === String ? JSON.parse(content) : content;
    } catch (error) {
        throw new Error(`JSON Parsing fails for content -- ${content}`);
    }
};

export const isEmptyOrNullObject = (obj: object | undefined | null): boolean => {
    return !obj || Object.keys(obj).length <= 0;
};

export const returnEmptyPromise = (): Promise<{}> => {
    return new Promise((resolve: (value?: {} | PromiseLike<{}> | undefined) => void) => resolve());
};

const UNSAFE_CHARS_REGEXP = /[<>\/\u2028\u2029]/g;
const ESCAPED_CHARS: { [unsafechar: string]: string } = {
    '<': '\\u003C',
    '>': '\\u003E',
    '/': '\\u002F',
    '\u2028': '\\u2028',
    '\u2029': '\\u2029'
};
const escapeUnsafeChars = (unsafeChar: string): string => {
    return ESCAPED_CHARS[unsafeChar];
};

export const sanitizeForXss = (str: string): string => {
    return str.replace(UNSAFE_CHARS_REGEXP, escapeUnsafeChars);
};

export const removeInstanceCache = (_key: string, value: { instance: string }): undefined | unknown => {
    if (value && value.instance === 'instance') {
        return undefined;
    }
    return value;
};

export const isAuthoringEditEnabled = (context: IRequestContext | undefined): boolean => {
    if (context) {
        if (context.features && context.features.authoring_inline_edit && context.params && !!context.params.isEditor) {
            return true;
        }
    }

    return false;
};

/**
 * Tries to parse a string value into an number and reurns this value. If the string is undefined
 * or if the string does not represent a valid number, undefined is returned
 *
 * @param value The string representing the number to be parsed
 */
export const tryParseInt = (value: string | undefined): number | undefined => {
    if (value) {
        const parsedValue = parseInt(value, 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
    }
    return undefined;
};

/**
 * Name of internal component fragment, must be all lower case
 */
export const SDK_FRAGMENT_NAME = 'platform.saferendermodule';

/**
 * Regex constant for capturing the internal fragment name with data-reactroot that is occasionally emitted
 */
export const SDK_FRAGMENT_NAME_REGEX = /<[/]?platform\.saferendermodule( data-reactroot=["].*["])?>/gi;

/**
 * Hepler function to build a URL to get the asset
 * @param assetPath the path to the asset
 */
export const getAsset = (assetPath: string, context: IRequestContext) => {
    let assetUrl: string = '';
    if (context.url.staticCdnUrl) {
        assetUrl += context.url.staticCdnUrl;
    }
    if (assetPath.length > 0 && assetPath.charAt(0) === '/') {
        assetPath = assetPath.substr(1);
    }
    const submissionId = msdyn365Commerce.getEnvironmentVariable('SUBMISSIONID');
    if (!submissionId || submissionId === 'undefined') {
        return `${assetUrl}${`${assetPath}`.replace(/\/+/g, '/')}`;
    } else {
        return `${assetUrl}${`${submissionId}/${assetPath}`.replace(/\/+/g, '/')}`;
    }
};

export const CurrencyCodeIdentifier = 'cc';
export const getCurrencyCodeFromContext = (requestContext: IRequestContext) => {
    return (
        (requestContext && requestContext.query && requestContext.query[CurrencyCodeIdentifier]) ||
        (requestContext.cookies && requestContext.cookies.get<string>(CurrencyCodeIdentifier).value) ||
        (requestContext.channel && requestContext.channel.Currency) ||
        getCurrency(requestContext.locale)
    );
};

export const isNonceEnabled = (requestContext: IRequestContext) => {
    const contentSecurityPolicy = requestContext.app && requestContext.app.contentSecurityPolicy;
    const nonceToken = contentSecurityPolicy && contentSecurityPolicy.nonceToken;
    return contentSecurityPolicy && !contentSecurityPolicy.disableContentSecurityPolicy && contentSecurityPolicy.enableNonce && nonceToken;
};

export const verboseLogger = (
    context: IRequestContext,
    telemetry: IInternalTelemetry | ITelemetry,
    messageTemplate: string,
    logOptions?: InternalTelemetryLogOptions | undefined,
    logLevel: LogLevel = LogLevel.Information
) => {
    if (context && context.features && !!context.features.enable_verbose_logs) {
        telemetry.log(logLevel, messageTemplate, logOptions);
    }
};

export const isVerboseLogEnabled = (context: IRequestContext): boolean => {
    return context && context.features && !!context.features.enable_verbose_logs;
};

export const checkVersionForFeature = (feature: string): boolean => {
    if (msdyn365Commerce.getVersionDependentFeatureInfo && msdyn365Commerce.getVersionDependentFeatureInfo[feature]) {
        return msdyn365Commerce.getVersionDependentFeatureInfo[feature];
    }
    return false;
};

export const skipConsoleWarn = () => {
    const originalConsole = { ...console };
    let showDebug = true;
    if (process.env.NODE_ENV !== 'development') {
        showDebug = false;
    }
    console.warn = (message: string) => {
        if (showDebug) {
            originalConsole.warn(message);
        }
    };
};

export const skipConsoleInfo = () => {
    const originalConsole = { ...console };
    let showDebug = true;
    if (process.env.NODE_ENV !== 'development') {
        showDebug = false;
    }
    // eslint-disable-next-line no-console
    console.info = (message: string) => {
        if (showDebug) {
            originalConsole.info(message);
        }
    };
};

/**
 * Returns true if the given string matches an URL pattern
 *
 * @param source The string to check against
 */
export const isAbsoluteUrl = (source: string): boolean => {
    return /^(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?$/.test(source);
};

// isOBORequest representing whether current user is on behalf of or not
export const isOboRequest = (requestContext: IRequestContext): boolean => {
    const { user } = requestContext;
    return user.isOBORequest || false;
};

// get customer account number
export const getCustomerAccountNumber = (requestContext: IRequestContext): object => {
    const accountSelectionCookie = requestContext?.cookies?.getAccountSelectionCookie();
    const isOBORequest = isOboRequest(requestContext);
    if (!isEmptyOrWhitespace(accountSelectionCookie) && isOBORequest) {
        const accountSelectionCookieValue = accountSelectionCookie;
        const accountSelectionCustomerIdParts: string[] = accountSelectionCookieValue.split(':');
        return { can: accountSelectionCustomerIdParts[1], isObo: true };
    }
    return { can: undefined, isObo: false };
};

// get customer account number for both B2B and B2C.
export const getCustomerAccountNumberForAllChannelType = (requestContext: IRequestContext): string | undefined => {
    if (isChannelTypeB2B(requestContext)) {
        const customerAccountNumber = getCustomerAccountNumber(requestContext) as { can: string; isObo: boolean };
        return customerAccountNumber.isObo ? customerAccountNumber.can : requestContext.user.customerAccountNumber;
    }
    return requestContext.user.customerAccountNumber;
};

/**
 * Verifies whether the string is empty or whitespace, but not null.
 *
 * @param {string} string The string.
 * @return {boolean} True if the string is empty or whitespace, false otherwise.
 */
const isEmptyOrWhitespace = (str: string): boolean => {
    if (str && typeof str !== 'string') {
        // Tracer.Error("StringExtensions.isEmptyOrWhitespace() has received input parameter not of type string.");
    }
    if (str) {
        // Make sure type string
        str = str.toString();

        return typeof str.trim === 'function' && (str.trim() || '').length === 0;
    }
    return false;
};

/**
 * Verifies whether the current channel is of type b2b.
 *
 * @param requestContext Current request context.
 * @return {boolean} True if the channel type is b2b.
 */
export const isChannelTypeB2B = (requestContext: IRequestContext): boolean => {
    return requestContext.apiSettings?.channelCustomerType?.toLowerCase() === 'b2b';
};

/**
 * Verifies whether the current context is of b2b.
 *
 * @param requestContext Current request context.
 * @return {boolean} True if either its oboRequest or user type is b2b.
 */
export const isUserTypeB2B = (requestContext: IRequestContext): boolean => {
    return requestContext.user?.isB2b || false || requestContext.user?.isOBORequest || false;
};
